import { useState, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Search,
  Calendar,
  Clock,
  Users,
  FileText,
  Play,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useTranscripts, useDeleteTranscript, useFilterOptions } from '@/hooks/useTranscripts'
import { formatDateTime, formatDuration } from '@/lib/utils'
import type { Transcript } from '@/types'

const ITEMS_PER_PAGE = 20

export function TranscriptsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [deleteDialog, setDeleteDialog] = useState<Transcript | null>(null)

  // Get filter values from URL
  const search = searchParams.get('search') || ''
  const roomName = searchParams.get('room_name') || ''
  const participant = searchParams.get('participant') || ''
  const startDate = searchParams.get('start_date') || ''
  const endDate = searchParams.get('end_date') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)

  const offset = (page - 1) * ITEMS_PER_PAGE

  const { data, isLoading, error } = useTranscripts({
    search: search || undefined,
    room_name: roomName || undefined,
    participant: participant || undefined,
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    limit: ITEMS_PER_PAGE,
    offset,
  })

  const { data: filterOptions } = useFilterOptions()
  const deleteMutation = useDeleteTranscript()

  const totalPages = useMemo(() => {
    if (!data?.total) return 1
    return Math.ceil(data.total / ITEMS_PER_PAGE)
  }, [data?.total])

  const updateFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) {
      params.set(key, value)
    } else {
      params.delete(key)
    }
    params.set('page', '1') // Reset to first page when filtering
    setSearchParams(params)
  }

  const goToPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams)
    params.set('page', newPage.toString())
    setSearchParams(params)
  }

  const handleDelete = async () => {
    if (!deleteDialog) return
    try {
      await deleteMutation.mutateAsync(deleteDialog.id)
      setDeleteDialog(null)
    } catch (error) {
      console.error('Failed to delete transcript:', error)
    }
  }

  const clearFilters = () => {
    setSearchParams({})
  }

  const hasFilters = search || roomName || participant || startDate || endDate

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Reuniões</h1>
        <p className="text-muted-foreground">
          Visualize e gerencie as transcrições de reuniões
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar no texto..."
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={roomName}
            onChange={(e) => updateFilter('room_name', e.target.value)}
            className="h-10 w-44 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Todas as salas</option>
            {filterOptions?.rooms?.map((room) => (
              <option key={room} value={room}>{room}</option>
            ))}
          </select>
          <select
            value={participant}
            onChange={(e) => updateFilter('participant', e.target.value)}
            className="h-10 w-48 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Todos participantes</option>
            {filterOptions?.participants?.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => updateFilter('start_date', e.target.value)}
            className="w-36"
            placeholder="Data início"
          />
          <Input
            type="date"
            value={endDate}
            onChange={(e) => updateFilter('end_date', e.target.value)}
            className="w-36"
            placeholder="Data fim"
          />
          {hasFilters && (
            <Button variant="ghost" size="icon" onClick={clearFilters} title="Limpar filtros">
              <Filter className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Results count */}
      {data && (
        <div className="text-sm text-muted-foreground">
          {data.total === 0
            ? 'Nenhuma reunião encontrada'
            : `Mostrando ${offset + 1}-${Math.min(offset + ITEMS_PER_PAGE, data.total)} de ${data.total} reuniões`}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center h-64 text-destructive">
          Erro ao carregar reuniões
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sala</TableHead>
                <TableHead>Data/Hora</TableHead>
                <TableHead>Duração</TableHead>
                <TableHead>Participantes</TableHead>
                <TableHead>Resumo</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data?.data || data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                      <p className="text-muted-foreground">Nenhuma reunião encontrada</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.data.map((transcript) => (
                  <TableRow key={transcript.id}>
                    <TableCell>
                      <Link
                        to={`/transcripts/${transcript.id}`}
                        className="font-medium hover:text-primary transition-colors"
                      >
                        {transcript.room_name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {formatDateTime(transcript.start_time)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {formatDuration(transcript.duration_seconds)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {transcript.participants.length}
                      </div>
                    </TableCell>
                    <TableCell>
                      {transcript.summary ? (
                        <Badge variant="success">Disponível</Badge>
                      ) : (
                        <Badge variant="secondary">-</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="default" size="sm" asChild>
                          <Link to={`/transcripts/${transcript.id}`}>
                            <Play className="h-4 w-4 mr-1" />
                            Ver Detalhes
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteDialog(transcript)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(page - 1)}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => goToPage(page + 1)}
            disabled={page === totalPages}
          >
            Próxima
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a transcrição da reunião{' '}
              <strong>{deleteDialog?.room_name}</strong>? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
